/**
 * CurrencySelect Component
 * 
 * Shared, reusable currency selector for consistent UI across the platform.
 * Supports all currencies defined in CURRENCY_OPTIONS constant.
 * 
 * Usage:
 * <CurrencySelect value="AUD" onChange={setCurrency} />
 * <CurrencySelect value="USD" onChange={setCurrency} variant="compact" />
 */

import React from 'react';
import { CurrencyCode } from '../../types/currency.types';
import { CURRENCY_OPTIONS } from '../../constants/currencies';

interface CurrencySelectProps {
  value: CurrencyCode;
  onChange: (currency: CurrencyCode) => void;
  disabled?: boolean;
  variant?: 'default' | 'compact';
  compactTone?: 'default' | 'inverted';
  className?: string;
}

const CurrencySelect: React.FC<CurrencySelectProps> = ({
  value,
  onChange,
  disabled = false,
  variant = 'default',
  compactTone = 'default',
  className = '',
}) => {
  if (variant === 'compact') {
    const isInverted = compactTone === 'inverted';
    const arrowStroke = isInverted ? '%23FFFFFF' : '%230F335B';
    const compactToneClasses = isInverted
      ? 'h-6 min-h-[1.5rem] pl-2.5 pr-6 py-0 text-[10px] font-semibold uppercase tracking-[0.08em] border border-white/25 rounded-md bg-transparent text-white leading-none focus:border-white/60 focus:ring-1 focus:ring-white/25'
      : 'text-sm font-medium border border-primary-500/20 rounded-lg bg-white text-primary-500 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20';
    const compactInteractionClasses = isInverted
      ? (disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-white/45')
      : (disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-accent-500/50');
    const compactPaddingClasses = isInverted ? '' : 'pl-3 pr-8 py-2';

    // Compact dropdown for tight spaces
    return (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as CurrencyCode)}
        disabled={disabled}
        className={`${compactPaddingClasses} outline-none transition-all appearance-none ${compactToneClasses} ${compactInteractionClasses} ${className}`}
        style={{
          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='${arrowStroke}' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
          backgroundPosition: isInverted ? 'right 0.3rem center' : 'right 0.5rem center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: isInverted ? '1rem 1rem' : '1.25rem 1.25rem'
        }}
      >
        {CURRENCY_OPTIONS.map((option) => (
          <option
            key={option.value}
            value={option.value}
            style={{ color: '#0f172a', backgroundColor: '#ffffff' }}
          >
            {option.value}
          </option>
        ))}
      </select>
    );
  }

  // Default variant with full labels
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as CurrencyCode)}
      disabled={disabled}
      className={`pl-4 pr-10 py-2.5 text-sm font-medium border border-primary-500/20 rounded-xl focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 outline-none transition-all bg-white text-primary-500 appearance-none ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-accent-500/50'
      } ${className}`}
      style={{
        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%230F335B' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
        backgroundPosition: 'right 0.75rem center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '1.25rem 1.25rem'
      }}
    >
      {CURRENCY_OPTIONS.map((option) => (
        <option
          key={option.value}
          value={option.value}
          style={{ color: '#0f172a', backgroundColor: '#ffffff' }}
        >
          {option.symbol} {option.value} - {option.label.split(' - ')[1]}
        </option>
      ))}
    </select>
  );
};

export default CurrencySelect;
